import { useEffect, useState } from 'react';
import { recordedAmount } from '../form/amountFields';
import { GroupMember } from '../grouping';
import { CashflowModalShell, SummaryFigure } from '../redesign/modal/CashflowModalShell';
import { LifeEventRow } from '../rows';
import { ownersFrom } from './jointAllocations';
import { ownersOf } from './owners';
import { useJointOwnership } from './useJointOwnership';
import { JointDeletion, JointLifeEventSubmission } from './useJointSaves';

type JointLifeEventModalProps = {
  opened: boolean;
  onClose: () => void;
  onSubmit: (submission: JointLifeEventSubmission) => Promise<void>;
  members: GroupMember[];
  defaultClientId?: string;
  /** The client whose own tab the form was opened from; absent on the family tab. */
  viewingClientId?: string;
  initialRow?: LifeEventRow;
  onDelete: (deletion: JointDeletion) => Promise<void>;
};

/** A life event is one cost, held in shares by the people it applies to. */
export function JointLifeEventFormModal({
  opened,
  onClose,
  onSubmit,
  members,
  defaultClientId,
  viewingClientId,
  initialRow,
  onDelete,
}: JointLifeEventModalProps) {
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [details, setDetails] = useState('');
  const [cost, setCost] = useState<number | ''>('');

  const stored = initialRow?.items[0];
  const storedOwners = stored ? ownersOf(stored) : undefined;
  const ownership = useJointOwnership({
    opened,
    noun: 'life event',
    storedId: stored?.id,
    storedOwners,
    members,
    defaultClientId,
    viewingClientId,
    onDelete,
  });
  const { allocations } = ownership;

  useEffect(() => {
    if (!opened) return;
    setDescription(initialRow?.description ?? '');
    setEventDate(initialRow?.eventDate?.split('T')[0] ?? '');
    setDetails(initialRow?.details ?? '');
    setCost(initialRow?.cost ?? '');
  }, [opened, initialRow]);

  const wholeCost = recordedAmount(cost);
  // The cost is on screen as typed, so the summary holds only the parts of it the form does not
  // show anywhere else: this client's share as the figure, with what lands outside the family
  // under it, or that part alone where the form is not opened from a client's tab.
  const viewerPart = ownership.viewerPart(wholeCost);
  const outsidePart = ownership.outsidePart(wholeCost);
  const summary: SummaryFigure[] = viewerPart
    ? [{ ...viewerPart, parts: outsidePart ? [outsidePart] : [] }]
    : ownership.shareParts(wholeCost);
  const handleSubmit = () => {
    if (ownership.isSaving) return;
    return ownership.save(() =>
      onSubmit({
        id: stored?.id,
        description,
        eventDate,
        details,
        cost: wholeCost,
        owners: ownersFrom(allocations, storedOwners),
        storedClientIds: ownership.storedClientIds,
      }),
    );
  };

  return (
    <CashflowModalShell
      opened={opened}
      onClose={onClose}
      title={initialRow ? 'Edit life event' : 'Add life event'}
      submitLabel={initialRow ? 'Save life event' : 'Add life event'}
      onSubmit={handleSubmit}
      onFieldChange={ownership.clearRefusal}
      isSaving={ownership.isSaving}
      notice={ownership.notice}
      summary={summary}
      testId="add-life-event-modal"
      fields={[
        [
          {
            kind: 'text',
            label: 'Description',
            value: description,
            onChange: setDescription,
            required: true,
          },
          { kind: 'date', label: 'Date', value: eventDate, onChange: setEventDate, required: true },
        ],
        [
          {
            kind: 'text',
            label: 'Details',
            value: details,
            onChange: setDetails,
            multiline: true,
          },
          { kind: 'money', label: 'Cost', value: cost, onChange: setCost },
        ],
      ]}
      allocation={{
        ...ownership.allocationProps,
        share: { amountLabel: 'Cost', of: wholeCost, assumed: ownership.assumedShare },
        extraFields: [],
      }}
      deletion={ownership.deletion}
    />
  );
}
